/**
 * Business Inquiries Management Page
 * /businesses/:id/inquiries - 문의 관리 페이지
 */

import { useLocation, useRoute } from "wouter";
import { ArrowLeft } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PageContainer } from "@/components/layout/PageContainer";
import Seo from "@/components/seo/Seo";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { BusinessInquiryManagement } from "@/components/business/BusinessInquiryManagement";
import { unwrapPayload } from "@/lib/queryClient";
import { buildLoginPath } from "@/lib/auth-navigation";

interface Business {
  id: string;
  name: string;
}

export default function BusinessInquiriesManagementPage() {
  const { user } = useAuth();
  const [, navigate] = useLocation();
  const [match, params] = useRoute<{ id: string }>("/businesses/:id/inquiries");
  const businessId = params?.id;

  // 업체 정보 조회
  const { data: business, isLoading: businessLoading } = useQuery<Business>({
    queryKey: ["/api/businesses", businessId],
    queryFn: async () => {
      if (!businessId) throw new Error("Invalid business ID");
      const response = await fetch(`/api/businesses/${businessId}`);
      if (!response.ok) throw new Error("Failed to fetch business");
      return unwrapPayload<Business>(await response.json());
    },
    enabled: !!businessId,
  });

  if (!match || !params || !businessId) {
    return (
      <PageContainer size="read">
        <Card className="p-12 text-center">
          <p className="text-muted-foreground">유효하지 않은 업체 ID입니다</p>
          <Button className="mt-4" onClick={() => navigate("/business-directory")}>
            목록으로
          </Button>
        </Card>
      </PageContainer>
    );
  }

  if (businessLoading) {
    return (
      <PageContainer size="read">
        <div className="flex items-center justify-center py-12">
          <p className="text-muted-foreground">로딩 중...</p>
        </div>
      </PageContainer>
    );
  }

  if (!business) {
    return (
      <PageContainer size="read">
        <Card className="p-12 text-center">
          <p className="text-muted-foreground">업체를 찾을 수 없습니다</p>
          <Button className="mt-4" onClick={() => navigate("/business-directory")}>
            목록으로
          </Button>
        </Card>
      </PageContainer>
    );
  }

  if (!user) {
    return (
      <PageContainer size="read">
        <Card className="p-12 text-center">
          <p className="text-muted-foreground">로그인이 필요합니다</p>
          <Button className="mt-4" onClick={() => navigate(buildLoginPath())}>
            로그인하기
          </Button>
        </Card>
      </PageContainer>
    );
  }

  return (
    <>
      <Seo
        title={`${business.name} - 문의 관리`}
        description="받은 문의를 관리하세요"
        path={`/businesses/${businessId}/inquiries`}
      />
      <PageContainer size="read">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(`/business-directory/${businessId}`)}
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">문의 관리</h1>
              <p className="text-muted-foreground">{business.name}</p>
            </div>
          </div>

          {/* Inquiry Management Component */}
          <BusinessInquiryManagement businessId={businessId} />
        </div>
      </PageContainer>
    </>
  );
}
